import { useEffect, useRef, useState } from 'react'
import { ScanLine, Trophy } from 'lucide-react'
import { RevealCurtain, useRevealCountdown } from './RevealCountdown'
import { CEREMONY_SUBTITLE, EVENT_FOOTER, StageChrome, type StagePalette } from './StageChrome'
import { cn } from '../../lib/cn'
import { stageSound } from '../../lib/stageAudio'
import type { AwardView } from '../admin/screenTypes'

/**
 * Most Active School — the booths that drew the most students.
 *
 * Its own stage rather than the podium's, for two reasons the Committee gave:
 *
 *   - **Two schools, counted rather than judged.** Second place is announced
 *     first and the hall ends on the winner, the same build-up a podium has —
 *     but there is no third, and the figure on screen is a tally of scans.
 *   - **It should not look like the others.** By this point in the ceremony
 *     the hall has watched a card open three times.
 *
 * So the reveal is a sequence rather than a card: the ten-second curtain
 * lifts, the winning school's name is **typed out** a character at a time
 * with a blinking caret, and only once it has landed does the visit count
 * roll up from zero to the winning figure.
 *
 * The curtain is kept from the other awards because it belongs to the
 * compère rather than to the award — it is the window in which the
 * announcement is made. What follows it is this award's own.
 */

/** Warm amber, so the hall can tell from the back that the award changed. */
const PALETTE: StagePalette = {
  background:
    'radial-gradient(ellipse at 50% 40%, rgba(255,214,150,0.20), transparent 58%), repeating-conic-gradient(from 214deg at 50% 42%, rgba(255,255,255,0.05) 0deg 6deg, transparent 6deg 17deg), linear-gradient(125deg, #7A4710 0%, #B2731E 44%, #4F2B09 100%)',
  border: '#361D05',
  sparkle: '#FFE39A',
  sparkleAlt: '#F2B15C',
}

/**
 * How long the number takes to climb.
 *
 * Long enough to read as a tally being counted rather than a figure being
 * printed, short enough that the hall is not waiting on it. Eased out, so it
 * races away and settles rather than ticking at a constant rate — a linear
 * count looks like a loading bar.
 */
const ROLL_MS = 2200

/**
 * Milliseconds per character as the winner's name is typed out.
 *
 * The countdown tells the hall *something* is coming but not what, and the
 * name used to simply be there the instant the curtain lifted — which from
 * the back of the room reads as a slide changing. Typing it holds the room
 * for the two or three seconds the compère needs to say it.
 *
 * 55ms puts the longest school name — "School of Computer Science and
 * Information Technology", 53 characters — at just under three seconds.
 */
const TYPE_MS = 55

/** Someone who has asked for less motion gets the name whole, immediately. */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true

/**
 * Reveals `text` one character at a time once `run` turns true.
 *
 * Timed against a real clock rather than a fixed step per interval, for the
 * same reason the count is: the projector laptop is also running the console
 * and an SSE stream, and a stalled frame should cost smoothness rather than
 * leave half a school's name on a 3-metre screen.
 */
function useTypewriter(text: string, run: boolean): { shown: string; done: boolean } {
  const [count, setCount] = useState(0)
  const frame = useRef<number>(0)

  useEffect(() => {
    if (!run) {
      setCount(0)
      return
    }
    if (prefersReducedMotion()) {
      setCount(text.length)
      return
    }

    const started = performance.now()

    const step = (now: number) => {
      const chars = Math.min(text.length, Math.floor((now - started) / TYPE_MS))
      setCount(chars)
      if (chars < text.length) frame.current = requestAnimationFrame(step)
    }

    frame.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame.current)
  }, [text, run])

  return { shown: text.slice(0, count), done: count >= text.length }
}

/**
 * Counts from zero to `target`, once, when `run` turns true.
 *
 * Driven by `requestAnimationFrame` against a real timestamp rather than a
 * fixed step per frame: the projector laptop also runs the admin console and
 * an SSE stream, and a dropped frame should cost smoothness, never make the
 * count end on the wrong number.
 */
function useRollingCount(target: number, run: boolean): number {
  const [value, setValue] = useState(0)
  const frame = useRef<number>(0)

  useEffect(() => {
    if (!run) {
      setValue(0)
      return
    }

    const started = performance.now()

    const step = (now: number) => {
      const progress = Math.min(1, (now - started) / ROLL_MS)
      // easeOutCubic
      const eased = 1 - (1 - progress) ** 3
      setValue(Math.round(target * eased))
      if (progress < 1) frame.current = requestAnimationFrame(step)
    }

    frame.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame.current)
  }, [target, run])

  return value
}

/** 2nd, then 1st — the award names two schools, not a podium of three. */
const PLACE_LABEL = ['', 'Most visited booth of the day', 'Second most visited booth'] as const

export function ActiveSchoolScreen({ award }: { award: AwardView }) {
  /*
   * The school just announced, whichever place it was.
   *
   * The award used to have one winner and this read `place === 1`. It names
   * two now — 2nd, then 1st — so the screen follows whichever opened last
   * rather than any fixed place: the runner-up gets the same full
   * announcement, and the hall still ends on the winner.
   */
  const revealedPlaces = award.results
    .filter((r) => r.revealed && r.revealedAt)
    .sort((a, b) => {
      if (a.revealedAt !== b.revealedAt) return b.revealedAt! > a.revealedAt! ? 1 : -1
      /*
       * Same second. MySQL `datetime` keeps whole seconds, so two reveals can
       * carry an identical stamp — and then "most recent" is a coin toss that
       * decides which school holds the screen. The award counts down, so the
       * lower place number is the one announced later.
       */
      return a.place - b.place
    })

  const winner = revealedPlaces[0]
  const revealed = winner != null

  /** Announced earlier, kept on screen underneath. */
  const earlier = revealedPlaces.slice(1).sort((a, b) => a.place - b.place)

  const { pending, count } = useRevealCountdown(
    revealed ? `active:${winner.place}:${winner.revealedAt}` : null,
    'gold',
    winner?.revealedAt ?? null,
    /* The same award fanfare every other place gets. */
    () => stageSound.awardReveal(),
  )

  const counting = pending != null
  /** Everything downstream starts when the curtain lifts, not on arrival. */
  const open = revealed && !counting

  /*
   * The beat: the curtain lifts, the name types itself out, and only then
   * does the count start climbing. Sequenced rather than simultaneous — two
   * things animating at once on a projector is noise, and the name is the
   * announcement while the number is the evidence for it.
   */
  const name = useTypewriter(winner?.label ?? '', open)
  const scans = useRollingCount(winner?.metric?.value ?? 0, open && name.done)

  return (
    <StageChrome
      palette={PALETTE}
      title={award.title}
      subtitle={CEREMONY_SUBTITLE}
      status={
        award.complete
          ? 'Both schools announced'
          : revealed
            ? `Announced ${revealedPlaces.length} of ${award.places}`
            : 'Awaiting announcement'
      }
      footerLeft={EVENT_FOOTER}
    >
      <div className="flex w-full flex-col items-center px-[4vmin] text-center">
        {!open ? (
          /*
           * Held shut until the curtain lifts, not merely until the server
           * reveals it. The curtain is 85% opaque, so anything rendered
           * underneath is faintly legible — and the winner is supposed to be
           * a secret for those ten seconds.
           *
           * Before the reveal there is nothing here to hide anyway: the
           * server withholds the school from the payload entirely.
           */
          <>
            <span className="grid size-[14vmin] place-items-center rounded-full border-[0.4vmin] border-dashed border-white/25">
              <ScanLine className="size-[6vmin] text-white/35" strokeWidth={1.6} />
            </span>
            <p className="mt-[3vmin] text-[3vmin] font-bold uppercase tracking-[0.3em] text-white/50">
              {counting ? 'Announcing now' : 'Counting the booths'}
            </p>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-[1.2vmin] rounded-full bg-[#FFE39A] px-[3vmin] py-[1vmin] text-[1.9vmin] font-extrabold uppercase tracking-[0.18em] text-[#5C320C] shadow-[0_5px_22px_rgba(255,214,150,0.3)]">
              <Trophy className="size-[2.2vmin]" strokeWidth={2.4} />
              {PLACE_LABEL[winner.place] ?? 'Most visited booth'}
            </span>

            {/*
              The name, typed out.

              The full string is rendered invisibly underneath to hold the
              box at its final size. Without it the heading grows line by
              line as the name arrives and shoves everything below it down
              the screen — on a projector that reads as the layout breaking,
              not as an animation.
            */}
            <h2
              className="relative mt-[2.6vmin] max-w-[86vw] text-balance text-[6.4vmin] font-extrabold leading-[1.05] text-white drop-shadow-[0_4px_18px_rgba(30,12,0,0.5)]"
              aria-label={winner?.label ?? undefined}
            >
              <span className="invisible" aria-hidden>
                {winner?.label}
              </span>
              <span className="absolute inset-0" aria-hidden>
                {name.shown}
                {!name.done && (
                  <span className="caret ml-[0.5vmin] inline-block h-[5.4vmin] w-[0.5vmin] translate-y-[0.7vmin] bg-[#FFE39A]" />
                )}
              </span>
            </h2>

            {/* Held back until the name has landed, so the beat stays clean. */}
            {winner?.detail && (
              <p
                className={cn(
                  'mt-[1.2vmin] text-[2.4vmin] font-semibold uppercase tracking-[0.22em] text-[#FFE39A] transition-opacity duration-500',
                  name.done ? 'opacity-100' : 'opacity-0',
                )}
              >
                {winner.detail}
              </p>
            )}

            {/*
              The count, rolling. `tabular-nums` is load-bearing: without it
              every digit change re-measures the line and the whole number
              jitters sideways for two seconds on a 3-metre screen.
            */}
            <div
              className={cn(
                'transition-opacity duration-500',
                name.done ? 'opacity-100' : 'opacity-0',
              )}
            >
              <p className="mt-[3.4vmin] text-[13vmin] font-black leading-none tabular-nums text-white drop-shadow-[0_6px_26px_rgba(30,12,0,0.55)]">
                {scans.toLocaleString()}
              </p>
              <p className="mt-[0.6vmin] text-[2.6vmin] font-bold uppercase tracking-[0.3em] text-white/75">
                {winner?.metric?.value === 1
                  ? (winner?.metric?.unit ?? 'student visit')
                  : (winner?.metric?.unitPlural ?? 'student visits')}
              </p>

              <p className="mt-[2.6vmin] text-[3.2vmin] font-bold italic text-[#FFE39A]">
                Congratulations!
              </p>

              {/*
                The school announced before this one, kept visible.

                Smaller and quieter: it has had its moment, and by the end the
                hall should see both together rather than the first one having
                vanished when the second arrived.
              */}
              {earlier.map((p) => (
                <p
                  key={p.place}
                  className="mt-[2vmin] text-[2.3vmin] font-semibold text-white/60"
                >
                  <span className="text-[#FFE39A]">{PLACE_LABEL[p.place]}</span>
                  {' · '}
                  {p.label}
                  {p.metric ? ` · ${p.metric.value.toLocaleString()} ${p.metric.unitPlural}` : ''}
                </p>
              ))}
            </div>
          </>
        )}
      </div>

      {counting && winner && (
        <RevealCurtain
          title={award.title}
          /*
           * The place actually behind the curtain — `winner.place`.
           *
           * This read `results.find((r) => !r.revealed)` instead, and `results`
           * is ordered 1, 2. So while second place was counting down, the
           * curtain announced "Most visited booth of the day": the hall was
           * told it was about to see the winner, and then saw the runner-up.
           *
           * The row being revealed is already `winner` — the curtain is drawn
           * over it for ten seconds — so there is nothing to search for.
           */
          place={PLACE_LABEL[winner.place] ?? 'The winner'}
          count={count}
        />
      )}
    </StageChrome>
  )
}
